import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BookingStepper, TripSummaryCard } from '../components/quote-v2';
import {
  CountrySelector,
  PhoneInput,
  BillingSection,
  DetailsPriceSummary,
  MobileDetailsSummary,
} from '../components/details';
import { Input } from '../components/ui/input';
import { GENDER_OPTIONS, DEFAULT_COUNTRY_CODE, toE164 } from '../config/countriesConfig';
import { getAutoIncludedExtras } from '../config/extrasConfig';
import useClarityPageReady from '../hooks/useClarityPageReady';
import { ArrowLeft, AlertCircle, User, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

// Session storage key
const STORAGE_KEY = 'vipgez_passenger_details';

// Format date from raw strings
const formatDateForDisplay = (dateStr, timeStr) => {
  if (!dateStr) return 'Date not set';
  try {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
    const monthName = date.toLocaleDateString('en-GB', { month: 'short' });
    return `${weekday}, ${day} ${monthName} ${year} at ${timeStr || '00:00'}`;
  } catch (e) {
    return `${dateStr} ${timeStr || ''}`;
  }
};

const DetailsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Clarity page ready signal
  useClarityPageReady('passenger_details');

  // Session state
  const [sessionData, setSessionData] = useState(null);
  const [isSessionLoading, setIsSessionLoading] = useState(true);

  // Get session ID from URL
  const sessionId = searchParams.get('sid') || searchParams.get('session_id');

  // Fetch session data
  const fetchSession = useCallback(async () => {
    if (!sessionId) {
      setIsSessionLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/booking/${sessionId}`);
      const result = await res.json();
      
      if (result.success && result.session) {
        setSessionData(result.session);
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
    } finally {
      setIsSessionLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Form state - Zuppin payload compatible field names
  const [formData, setFormData] = useState(() => {
    // Try to restore from session storage
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved form data');
      }
    }
    return {
      // Passenger 1 (Contact Person) - Required fields
      firstName: '',
      lastName: '',
      nationality: DEFAULT_COUNTRY_CODE,
      phoneCountry: DEFAULT_COUNTRY_CODE,
      phoneNumber: '',
      email: '',
      flightNumber: '', // Optional - Flight number for pickup
      passengerNames: '', // Optional - Additional passenger names
      // Optional fields
      gender: '',
      idNumber: '',
      // Billing (all optional)
      billing: {
        billingName: '',
        taxNumber: '',
        billingAddress: '',
        billingCountry: '',
      },
    };
  });

  const [errors, setErrors] = useState({});
  
  // Processing states
  const [isProcessing, setIsProcessing] = useState(false);

  // Get trip details from session or URL params
  const tripDetails = useMemo(() => {
    if (sessionData) {
      const date = sessionData.trip_date || '';
      const time = sessionData.trip_time || '';
      return {
        pickup: sessionData.pickup_location || 'Istanbul Airport',
        dropoff: sessionData.dropoff_location || '',
        date: date,
        time: time,
        formattedDate: formatDateForDisplay(date, time),
        passengers: sessionData.passengers || 1,
        luggage: sessionData.luggage || 1,
        distance: '45',
        durationMinutes: '55',
        timezone: sessionData.timezone || 'Europe/Istanbul',
        pickupType: sessionData.pickup_type || 'manual',
      };
    }
    // Fallback to URL params
    return {
      pickup: searchParams.get('pickup') || 'Istanbul Airport (IST)',
      dropoff: searchParams.get('dropoff') || 'Taksim Square',
      date: searchParams.get('date') || '',
      time: searchParams.get('time') || '',
      formattedDate: '',
      passengers: parseInt(searchParams.get('passengers')) || 1,
      luggage: parseInt(searchParams.get('luggage')) || 1,
      distance: '45',
      durationMinutes: '55',
      pickupType: 'manual',
    };
  }, [sessionData, searchParams]);

  // Get vehicle details from session or URL params
  const vehicleDetails = useMemo(() => {
    // While loading, return null to show loading state
    if (isSessionLoading) {
      return null;
    }
    
    if (sessionData) {
      // Check for vehicle data in session (saved by Quote page)
      const vehicleSnapshot = sessionData.selected_vehicle_snapshot || sessionData.selected_vehicle;
      
      // Priority: session.vehicle_price > session.subtotal > snapshot price
      let vehiclePrice = sessionData.vehicle_price;
      if (!vehiclePrice && vehiclePrice !== 0) {
        vehiclePrice = sessionData.subtotal;
      }
      if (!vehiclePrice && vehiclePrice !== 0 && vehicleSnapshot) {
        vehiclePrice = vehicleSnapshot.price;
      }
      
      // Ensure it's a number
      vehiclePrice = parseFloat(vehiclePrice) || 0;
      
      if (vehicleSnapshot || vehiclePrice > 0) {
        return {
          id: vehicleSnapshot?.type || vehicleSnapshot?.vehicleType || vehicleSnapshot?.id || 'VAN',
          name: sessionData.vehicle_name || vehicleSnapshot?.name || vehicleSnapshot?.categoryDisplayName || 'VIP Van',
          price: vehiclePrice,
        };
      }
    }
    // Fallback to URL params only if session loaded but has no data
    return {
      id: searchParams.get('vehicleType') || 'VAN',
      name: searchParams.get('vehicle') || 'VIP Van',
      price: parseFloat(searchParams.get('price')) || 0,
    };
  }, [sessionData, searchParams, isSessionLoading]);

  const extrasTotal = parseInt(searchParams.get('extrasTotal')) || 0;

  // Parse extras from URL
  const selectedExtras = useMemo(() => {
    try {
      const extrasParam = searchParams.get('extras');
      return extrasParam ? JSON.parse(extrasParam) : [];
    } catch (e) {
      return [];
    }
  }, [searchParams]);

  // Total price calculation
  const totalPrice = (vehicleDetails?.price || 0) + extrasTotal;

  // Auto-included extras (gifts) - from extrasConfig
  const autoIncludedExtras = useMemo(() => {
    return getAutoIncludedExtras();
  }, []);

  // Save form data to session storage
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  // Handle form field change
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error on change
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Handle billing data change
  const handleBillingChange = (billingData) => {
    setFormData(prev => ({ ...prev, billing: billingData }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.nationality) {
      newErrors.nationality = 'Please select your nationality';
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    // Flight number is optional - no validation needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle continue to payment
  const handleContinue = async () => {
    if (!validateForm()) {
      toast.error('Please complete all required fields', {
        style: { background: '#FF8C00', color: 'white', border: 'none' }
      });
      return;
    }

    setIsProcessing(true);

    try {
      // Build phone in E164 format
      const phoneE164 = toE164(formData.phoneNumber, 
        require('../config/countriesConfig').getCountryByCode(formData.phoneCountry).dialCode
      );

      // Build extras array with full pricing info for backend
      const extrasForBackend = selectedExtras.map(extra => ({
        id: extra.id,
        code: extra.id,
        name: extra.name,
        price: extra.price || 0,
        quantity: extra.quantity || 1
      }));

      // Save details to backend session
      const response = await fetch(`${API_BASE}/api/booking/details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionId,
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: phoneE164,
          notes: formData.notes || '',
          flightNumber: formData.flightNumber || '',
          passengerNames: formData.passengerNames || '',
          extras: extrasForBackend // Include extras with full pricing info
        })
      });

      const data = await response.json();

      if (data.success) {
        // Navigate to Payment page
        navigate(`/payment?sid=${sessionId}`);
      } else {
        throw new Error(data.detail || 'Failed to save details');
      }
    } catch (error) {
      console.error('Save details error:', error);
      toast.error(error.message || 'Failed to save your details. Please try again.', {
        style: { background: '#FF8C00', color: 'white', border: 'none' }
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };

  const canContinue = formData.firstName && formData.lastName && formData.nationality && 
                      formData.phoneNumber && formData.email;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* noindex for booking flow pages */}
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      
      <Header />

      <main className="pt-28 md:pt-32 pb-32 lg:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <div className="mb-4">
            <Button
              variant="outline"
              onClick={handleBack}
              className="text-white border-white/30 hover:bg-white/10 hover:text-white bg-white/5"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Extras
            </Button>
          </div>

          {/* Booking Stepper - Step 3 Active */}
          <BookingStepper currentStep={3} />

          {/* Page Title */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white mb-2">Passenger Details</h1>
            <p className="text-gray-400">Please provide contact information for the booking</p>
          </div>

          {/* Main 3-Column Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left: Trip Summary */}
            <div className="lg:col-span-3 hidden lg:block">
              <div className="sticky top-28">
                <TripSummaryCard tripDetails={tripDetails} />
              </div>
            </div>

            {/* Center: Passenger Details Form */}
            <div className="lg:col-span-6 space-y-6">
              {/* Passenger 1 (Contact Person) */}
              <div className="bg-white rounded-xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-[#FF8C00] flex items-center justify-center">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-black">Passenger 1</h2>
                    <p className="text-xs text-gray-500">Contact Person</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* First Name */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-600">
                      First Name <span className="text-[#FF8C00]">*</span>
                    </label>
                    <Input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      placeholder="Enter first name"
                      className={`h-11 text-black placeholder:text-gray-400 ${errors.firstName ? 'border-[#FF8C00]' : ''}`}
                      data-testid="input-firstname"
                    />
                    {errors.firstName && (
                      <div className="flex items-center gap-1 text-[#FF8C00]">
                        <AlertCircle className="h-3 w-3" />
                        <span className="text-xs">{errors.firstName}</span>
                      </div>
                    )}
                  </div>

                  {/* Last Name */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-600">
                      Last Name <span className="text-[#FF8C00]">*</span>
                    </label>
                    <Input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      placeholder="Enter last name"
                      className={`h-11 text-black placeholder:text-gray-400 ${errors.lastName ? 'border-[#FF8C00]' : ''}`}
                      data-testid="input-lastname"
                    />
                    {errors.lastName && (
                      <div className="flex items-center gap-1 text-[#FF8C00]">
                        <AlertCircle className="h-3 w-3" />
                        <span className="text-xs">{errors.lastName}</span>
                      </div>
                    )}
                  </div>

                  {/* Nationality */}
                  <CountrySelector
                    value={formData.nationality}
                    onChange={(code) => handleChange('nationality', code)}
                    label="Nationality"
                    required
                    error={errors.nationality}
                  />

                  {/* Phone */}
                  <PhoneInput
                    value={formData.phoneNumber}
                    onChange={(value) => handleChange('phoneNumber', value)}
                    countryCode={formData.phoneCountry}
                    onCountryChange={(code) => handleChange('phoneCountry', code)}
                    label="Phone Number"
                    required
                    error={errors.phoneNumber}
                  />

                  {/* Email - Full Width */}
                  <div className="sm:col-span-2 space-y-1.5">
                    <label className="text-xs font-medium text-gray-600">
                      Email <span className="text-[#FF8C00]">*</span>
                    </label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="your@email.com"
                      className={`h-11 text-black placeholder:text-gray-400 ${errors.email ? 'border-[#FF8C00]' : ''}`}
                      data-testid="input-email"
                    />
                    {errors.email && (
                      <div className="flex items-center gap-1 text-[#FF8C00]">
                        <AlertCircle className="h-3 w-3" />
                        <span className="text-xs">{errors.email}</span>
                      </div>
                    )}
                  </div>

                  {/* Flight Number - Optional but recommended */}
                  <div className="sm:col-span-2 space-y-1.5">
                    <label className="text-xs font-medium text-gray-600">
                      Flight Number <span className="text-gray-400 text-[10px] font-normal">(recommended)</span>
                    </label>
                    <Input
                      type="text"
                      value={formData.flightNumber}
                      onChange={(e) => handleChange('flightNumber', e.target.value.toUpperCase())}
                      placeholder="e.g. TK1234, PC456"
                      className="h-11 text-black placeholder:text-gray-400"
                      data-testid="input-flightnumber"
                    />
                    <p className="text-xs text-gray-500">Your driver will track your flight for delays</p>
                  </div>

                  {/* Additional Passenger Names - Optional, shown when passengers > 1 */}
                  {tripDetails.passengers > 1 && (
                    <div className="sm:col-span-2 space-y-1.5">
                      <label className="text-xs font-medium text-gray-600">
                        Additional Passengers <span className="text-gray-400 text-[10px] font-normal">(optional)</span>
                      </label>
                      <textarea
                        value={formData.passengerNames}
                        onChange={(e) => handleChange('passengerNames', e.target.value)}
                        placeholder={"e.g.\nJohn Smith\nEmma Johnson"}
                        rows={Math.min(tripDetails.passengers - 1, 4)}
                        className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-black placeholder:text-gray-400 focus:outline-none focus:border-[#FF8C00] focus:ring-2 focus:ring-[#FF8C00]/20 resize-none"
                        data-testid="input-passenger-names"
                      />
                      <p className="text-xs text-gray-500">
                        Enter the names of the other {tripDetails.passengers - 1} passenger{tripDetails.passengers > 2 ? 's' : ''} (one per line) so your driver can prepare a name board
                      </p>
                    </div>
                  )}

                  {/* Gender (Optional) */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-600">
                      Gender
                    </label>
                    <select
                      value={formData.gender}
                      onChange={(e) => handleChange('gender', e.target.value)}
                      className="w-full h-11 px-3 bg-white border border-gray-200 rounded-lg text-sm text-black focus:outline-none focus:border-[#FF8C00] focus:ring-2 focus:ring-[#FF8C00]/20"
                      data-testid="input-gender"
                    >
                      {GENDER_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* ID/Passport (Optional) */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-600">
                      ID / Passport Number
                    </label>
                    <Input
                      type="text"
                      value={formData.idNumber}
                      onChange={(e) => handleChange('idNumber', e.target.value)}
                      placeholder="Optional"
                      className="h-11 text-black placeholder:text-gray-400"
                      data-testid="input-idnumber"
                    />
                  </div>
                </div>
              </div>

              {/* Billing Section (Collapsed by default) */}
              <BillingSection
                data={formData.billing}
                onChange={handleBillingChange}
                errors={{}}
              />
            </div>

            {/* Right: Price Summary */}
            <div className="lg:col-span-3 hidden lg:block">
              <div className="sticky top-28">
                <DetailsPriceSummary
                  tripAmount={vehicleDetails?.price || 0}
                  extrasAmount={extrasTotal}
                  selectedVehicle={vehicleDetails}
                  selectedExtras={selectedExtras}
                  autoIncludedExtras={autoIncludedExtras}
                  onBack={handleBack}
                  onContinue={handleContinue}
                  canContinue={canContinue}
                  isProcessing={isProcessing}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Price Summary */}
      <MobileDetailsSummary
        totalAmount={totalPrice}
        onBack={handleBack}
        onContinue={handleContinue}
        canContinue={canContinue}
        isProcessing={isProcessing}
      />

      <Footer />
    </div>
  );
};

export default DetailsPage;
